// GetPaid locale detection + message aggregation.
//
// Messages are split into per-feature modules under ./messages
// (`<feature>.<locale>.ts`, each exporting a flat Record<string,string>) and
// merged per locale below. Adding a new feature module going forward is a
// two-line change: import it and spread it into both locale maps in the
// "Feature message modules" block.
import commonEn from './messages/common.en';
import commonPtBR from './messages/common.pt-BR';
import authEn from './messages/auth.en';
import authPtBR from './messages/auth.pt-BR';
import shellEn from './messages/shell.en';
import shellPtBR from './messages/shell.pt-BR';
import onboardingEn from './messages/onboarding.en';
import onboardingPtBR from './messages/onboarding.pt-BR';
import invoicesEn from './messages/invoices.en';
import invoicesPtBR from './messages/invoices.pt-BR';
import affiliateEn from './messages/affiliate.en';
import affiliatePtBR from './messages/affiliate.pt-BR';
import referralEn from './messages/referral.en';
import referralPtBR from './messages/referral.pt-BR';

export const SUPPORTED_LOCALES = ['en', 'pt-BR'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

// Brazil is the primary market for GetPaid, so a first-time visitor lands in
// pt-BR (mirrors apps/landing).
export const DEFAULT_LOCALE: Locale = 'pt-BR';

// EN is the message source of truth — every key exists in the `.en` module —
// so it's merged underneath the active locale to backstop any missing
// translation. This is the locale react-intl uses for `defaultLocale`, NOT
// necessarily what's shown.
export const FALLBACK_LOCALE: Locale = 'en';

// Query-string key that pins the language on a shared link, e.g.
// https://getpaid.caratuva.com/?lang=en.
export const LOCALE_PARAM = 'lang';

// Shared with the dashboard/landing so a returning user keeps one language
// choice across Caratuva properties.
const LOCAL_STORAGE_KEY = 'caratuva.locale';

// -------- Feature message modules --------
// Add one import pair + one spread entry per locale below as new feature
// modules land (auth, shell, onboarding, invoices, affiliate, ...).
const messagesByLocale: Record<Locale, Record<string, string>> = {
  en: {
    ...commonEn,
    ...authEn,
    ...shellEn,
    ...onboardingEn,
    ...invoicesEn,
    ...affiliateEn,
    ...referralEn,
  },
  'pt-BR': {
    ...commonPtBR,
    ...authPtBR,
    ...shellPtBR,
    ...onboardingPtBR,
    ...invoicesPtBR,
    ...affiliatePtBR,
    ...referralPtBR,
  },
};
// -------- End feature message modules --------

// Normalize a raw ?lang= value to a supported locale. Accepts the canonical
// form ('pt-BR'), any case ('pt-br'), or the bare language subtag ('pt', 'en',
// even 'en-US'). Returns null for anything we don't ship so the caller can
// fall back to its normal detection instead of guessing.
export function parseLocaleParam(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  const primary = lower.split('-')[0];
  for (const locale of SUPPORTED_LOCALES) {
    const locLower = locale.toLowerCase();
    if (locLower === lower || locLower.split('-')[0] === primary) return locale;
  }
  return null;
}

// Precedence: an explicit ?lang= on the URL (shared-link intent) wins over a
// returning visitor's stored choice, which wins over the browser language,
// which falls back to the pt-BR default.
export function resolveLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const fromUrl = parseLocaleParam(new URLSearchParams(window.location.search).get(LOCALE_PARAM));
  if (fromUrl) return fromUrl;
  const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as Locale;
  }
  const nav = window.navigator.language.toLowerCase();
  if (nav.startsWith('pt')) return 'pt-BR';
  if (nav.startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, locale);
}

// Messages for the given locale, merged over the EN fallback so a partial
// translation never surfaces a missing-key warning.
export function getMessagesForLocale(locale: Locale): Record<string, string> {
  return {
    ...messagesByLocale[FALLBACK_LOCALE],
    ...messagesByLocale[locale],
  };
}
